<div class="section-divider d-none d-md-flex align-items-center gap-3 mb-2">
  <div class="flex-grow-1 border-top"></div>
  <span class="fw-semibold small text-uppercase text-muted px-1" style="letter-spacing:.08em;">⚡ {{ 'TAB_ENERGIE' | translate }}</span>
  <div class="flex-grow-1 border-top"></div>
</div>

<!-- Live Solar Cards: section header (styles.css pattern (a): one row on desktop, title + controls rows on phones) -->
<div class="dash-section-head">
  <div class="dash-section-title">
    <span class="badge bg-danger dash-live"><span [style.opacity]="liveDotOn ? '1' : '0'">●</span> LIVE</span>
    <span class="dash-section-name">{{ 'AKTUELL' | translate }}</span>
    <span *ngIf="solarUpdatedAt" class="dash-section-age">· {{ 'UPDATED_AGO' | translate:{time: updatedAgo(solarUpdatedAt)} }}</span>
  </div>
  <div class="dash-section-controls">
    <ng-container *ngTemplateOutlet="rangeButtons"></ng-container>
    <span *ngIf="rangeLoading" class="spinner-border spinner-border-sm text-muted d-none d-sm-inline-block" role="status"></span>
    <button *ngIf="solarHistory.length" class="btn btn-sm btn-outline-secondary dash-icon-btn"
            [attr.aria-label]="'CHART_OPEN' | translate" (click)="openSolarChart.emit()">📈</button>
  </div>
</div>

<!-- Range buttons (section header and card popups). While a range loads, phones show the spinner inside the
     pressed button instead of next to the group, so the full-width segmented control keeps its width; from
     576 px the spinner beside the group remains. -->
<ng-template #rangeButtons let-cls="cls">
  <div class="btn-group btn-group-sm dash-seg dash-seg-xs" [ngClass]="cls" role="group">
    <button *ngFor="let r of chartRanges" class="btn"
            [ngClass]="range === r.key ? 'btn-secondary' : 'btn-outline-secondary'"
            [class.range-busy]="rangeLoading && range === r.key"
            (click)="selectRange(r.key)"><span class="range-label">{{ r.label | translate }}</span><span
            *ngIf="rangeLoading && range === r.key" class="spinner-border range-spin" aria-hidden="true"></span></button>
  </div>
</ng-template>

<!-- Card popup content (the card's name is the popup's title, [popupTitle]): range buttons, the expanded
     chart (16:9 from 576 px; on a phone it fills the free height of the full-screen sheet), min · range · max -->
<ng-template #solarChartTpl let-mode="mode">
  <div class="dash-sheet-fill solar-sheet">
    <div class="d-flex align-items-center gap-2 mb-2">
      <ng-container *ngTemplateOutlet="rangeButtons; context:{cls: 'ms-auto'}"></ng-container>
      <span *ngIf="rangeLoading" class="spinner-border spinner-border-sm text-muted d-none d-sm-inline-block" role="status"></span>
    </div>
    <app-sparkline-chart
      class="dash-sheet-fill-item"
      [solarPoints]="displaySolar"
      [solarMode]="$any(mode)"
      [darkMode]="darkMode"
      [expanded]="true"
      [sheetFill]="true"
      [compactOnPhone]="true"
      [windowMs]="windowMs"
      [linkId]="popupPath(mode)"
      style="display:block;">
    </app-sparkline-chart>
    <div *ngIf="displaySolar.length" class="d-flex justify-content-between mt-1 text-muted dash-meta">
      <span>{{ formatW(solarMinMax($any(mode)).min, 1) }}</span>
      <span>{{ rangeLabel }}</span>
      <span>{{ formatW(solarMinMax($any(mode)).max, 1) }}</span>
    </div>
  </div>
</ng-template>

<!-- Tap-overlay chart templates per metric. The card popovers reuse these via [tapTpl]; each popup's 📌
     ([boardChart] "solar.<mode>") puts its chart on the Analyse board. -->
<ng-template #pvTapTpl>
  <ng-container *ngTemplateOutlet="solarChartTpl; context:{mode:'pvYield'}"></ng-container>
</ng-template>

<ng-template #battTapTpl>
  <ng-container *ngTemplateOutlet="solarChartTpl; context:{mode:'battery'}"></ng-container>
</ng-template>

<ng-template #gridTapTpl>
  <ng-container *ngTemplateOutlet="solarChartTpl; context:{mode:'grid'}"></ng-container>
</ng-template>

<ng-template #consTapTpl>
  <ng-container *ngTemplateOutlet="solarChartTpl; context:{mode:'consumption'}"></ng-container>
</ng-template>

<!-- KPI cards: four per row on desktop, two from 576 px; phones stack them full width (the charts need the
     width: two y axes on the Akku card) with the value at the end of the label row (component styles) -->
<div class="row g-2 g-sm-3 mb-3" *ngIf="solar" [class.section-flash]="solarFlash">

  <!-- PV Ertrag -->
  <div class="col-6 col-lg-3 dash-chart-col">
    <app-popup-card cardClass="h-100 border-warning" popupMaxWidth="min(92vw, 1100px)"
                    [popupTitle]="'☀️ ' + ('PV_ERTRAG' | translate)"
                    [tapTpl]="solarHistory.length >= 2 ? pvTapTpl : undefined"
                    [linkId]="solarHistory.length >= 2 ? popupPath('pvYield') : null"
                    [boardChart]="boardChart('pvYield')">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0">☀️</div>
            <div class="min-w-0 kpi-text">
              <div class="text-muted small kpi-label">{{ 'PV_ERTRAG' | translate }}</div>
              <div class="card-value fs-5 fw-bold text-warning text-truncate">{{ formatW(solar.pvYieldW) }}</div>
              <div *ngIf="vs.sunrise" class="text-muted text-truncate dash-meta kpi-sub">🌅 {{ vs.sunrise }} · 🌇 {{ vs.sunset }}</div>
            </div>
          </div>
          <app-sparkline-chart *ngIf="displaySolar.length >= 2" class="kpi-chart" [cardChart]="true" [solarPoints]="displaySolar" [windowMs]="windowMs" [solarMode]="'pvYield'" [darkMode]="darkMode"></app-sparkline-chart>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Akku -->
  <div class="col-6 col-lg-3 dash-chart-col">
    <app-popup-card cardClass="h-100 border-success" popupMaxWidth="min(92vw, 1100px)"
                    [popupTitle]="'🔋 ' + ('AKKU' | translate)"
                    [tapTpl]="solarHistory.length >= 2 ? battTapTpl : undefined"
                    [linkId]="solarHistory.length >= 2 ? popupPath('battery') : null"
                    [boardChart]="boardChart('battery')">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0"
                 [class.battery-charging]="solar.batteryDischargeW < 0"
                 [class.battery-discharging]="solar.batteryDischargeW > 0">
              <ng-container *ngIf="solar.batteryDischargeW < 0">⚡</ng-container>
              <ng-container *ngIf="solar.batteryDischargeW > 0">🪫</ng-container>
              <ng-container *ngIf="solar.batteryDischargeW === 0">🔋</ng-container>
            </div>
            <div class="flex-grow-1 min-w-0 kpi-text">
              <div class="text-muted small kpi-label">{{ 'AKKU' | translate }}</div>
              <div class="card-value fs-5 fw-bold text-truncate"
                   [class.text-success]="solar.batteryDischargeW <= 0"
                   [class.text-danger]="solar.batteryDischargeW > 0">
                {{ formatW(-solar.batteryDischargeW) }}
              </div>
              <div class="progress mt-1" style="height:5px;">
                <div class="progress-bar"
                     [class.bg-success]="solar.batteryDischargeW === 0"
                     [class.progress-bar-charging]="solar.batteryDischargeW < 0"
                     [class.progress-bar-discharging]="solar.batteryDischargeW > 0"
                     [style.width.%]="solar.batteryPercent"></div>
              </div>
              <!-- state of charge · stored / capacity · time to full / empty: wraps on phones instead of "…" -->
              <div class="text-muted mt-1 text-truncate m-wrap kpi-batt kpi-sub">
                {{ solar.batteryPercent }}&nbsp;%
                <ng-container *ngIf="solar.batteryCapacityWh">
                  · {{ (solar.batteryPercent / 100 * solar.batteryCapacityWh / 1000).toFixed(1) }}&thinsp;/&thinsp;{{ (solar.batteryCapacityWh / 1000).toFixed(0) }}&thinsp;kWh
                </ng-container>
                <ng-container *ngIf="batteryRuntime(solar) as rt"> · <span class="text-nowrap">{{ rt }}</span></ng-container>
              </div>
            </div>
          </div>
          <app-sparkline-chart *ngIf="displaySolar.length >= 2" class="kpi-chart" [cardChart]="true" [solarPoints]="displaySolar" [windowMs]="windowMs" [solarMode]="'battery'" [darkMode]="darkMode"></app-sparkline-chart>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Netzbezug -->
  <div class="col-6 col-lg-3 dash-chart-col">
    <app-popup-card cardClass="h-100 border-info" popupMaxWidth="min(92vw, 1100px)"
                    [popupTitle]="'🔌 ' + ('NETZBEZUG' | translate)"
                    [tapTpl]="solarHistory.length >= 2 ? gridTapTpl : undefined"
                    [linkId]="solarHistory.length >= 2 ? popupPath('grid') : null"
                    [boardChart]="boardChart('grid')">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0">🔌</div>
            <div class="min-w-0 kpi-text">
              <div class="text-muted small kpi-label">{{ 'NETZBEZUG' | translate }}</div>
              <div class="card-value fs-5 fw-bold text-truncate"
                   [class.text-danger]="solar.gridConsumptionW > 0"
                   [class.text-success]="solar.gridConsumptionW <= 0">
                {{ formatW(solar.gridConsumptionW) }}
              </div>
              <div class="text-muted small kpi-sub">{{ (solar.gridConsumptionW > 0 ? 'BEZUG' : 'EINSPEISUNG') | translate }}</div>
            </div>
          </div>
          <app-sparkline-chart *ngIf="displaySolar.length >= 2" class="kpi-chart" [cardChart]="true" [solarPoints]="displaySolar" [windowMs]="windowMs" [solarMode]="'grid'" [darkMode]="darkMode"></app-sparkline-chart>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Verbrauch -->
  <div class="col-6 col-lg-3 dash-chart-col">
    <app-popup-card cardClass="h-100 border-primary" popupMaxWidth="min(92vw, 1100px)"
                    [popupTitle]="'🏠 ' + ('VERBRAUCH' | translate)"
                    [tapTpl]="solarHistory.length >= 2 ? consTapTpl : undefined"
                    [linkId]="solarHistory.length >= 2 ? popupPath('consumption') : null"
                    [boardChart]="boardChart('consumption')">
      <ng-template #body>
        <div class="card-body d-flex flex-column p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1">
            <div class="card-icon fs-2 flex-shrink-0">🏠</div>
            <div class="min-w-0 flex-grow-1 kpi-text">
              <div class="text-muted small kpi-label">{{ 'VERBRAUCH' | translate }}</div>
              <div class="card-value fs-5 fw-bold text-primary text-truncate">{{ formatW(solar.totalConsumptionW) }}</div>
            </div>
          </div>
          <div *ngIf="hasPhaseData(solar)" class="mt-1 kpi-phases">
            <div *ngFor="let ph of phaseData(solar)" class="d-flex align-items-center gap-1">
              <span class="fw-semibold" [style.color]="ph.color" style="width:1.6em;">{{ ph.label }}</span>
              <div class="flex-grow-1 position-relative" style="height:6px;background:rgba(128,128,128,.15);border-radius:3px;">
                <div [style.width.%]="ph.pct" [style.background]="ph.color" style="height:100%;border-radius:3px;transition:width .3s;"></div>
                <div style="position:absolute;top:0;height:100%;width:1px;" [style.background]="gridLimitColor" [style.left.%]="ph.gridBarrierPct" [title]="'ENERGIE_GRID_LIMIT_HINT' | translate"></div>
              </div>
              <span class="text-muted" style="min-width:3.5em;text-align:right;">{{ formatW(ph.watts) }}</span>
            </div>
          </div>
          <app-sparkline-chart *ngIf="displaySolar.length >= 2" class="kpi-chart" [cardChart]="true" [solarPoints]="displaySolar" [windowMs]="windowMs" [solarMode]="'consumption'" [darkMode]="darkMode"></app-sparkline-chart>
        </div>
      </ng-template>
    </app-popup-card>
  </div>

  <!-- Today's autarky summary: the fifth card would be alone in its row below 992 px, so it spans the row there -->
  <div class="col-12 col-lg-3" *ngIf="todayStat">
    <div class="card h-100" [class.border-success]="todayStat.autarkiePercent >= 80"
         [class.border-warning]="todayStat.autarkiePercent >= 50 && todayStat.autarkiePercent < 80"
         [class.border-danger]="todayStat.autarkiePercent < 50">
      <div class="card-body d-flex align-items-center gap-2 p-2 p-sm-3">
        <div class="card-icon fs-2 flex-shrink-0">🏡</div>
        <div class="min-w-0 flex-grow-1 kpi-text">
          <div class="d-flex align-items-baseline gap-1 min-w-0 kpi-label">
            <div class="text-muted small">{{ 'HEUTE_AUTARKIE' | translate }}</div>
            <div class="text-muted dash-meta text-truncate">{{ 'HEUTE_SEIT' | translate }}</div>
          </div>
          <div class="card-value fs-5 fw-bold text-truncate"
               [class.text-success]="todayStat.autarkiePercent >= 80"
               [class.text-warning]="todayStat.autarkiePercent >= 50 && todayStat.autarkiePercent < 80"
               [class.text-danger]="todayStat.autarkiePercent < 50">
            {{ Math.floor(todayStat.autarkiePercent) }}&thinsp;%
          </div>
          <!-- consumption · grid import: in the card where it is wide enough (below 992 px: full width; from
               1200 px), else (a quarter of 992–1199 px) in the footer -->
          <div class="text-muted text-truncate d-lg-none d-xl-block kpi-autarky-sub kpi-sub">
            ⚡&thinsp;{{ formatKwh(todayStat.consumptionKwh) }}
            &nbsp;·&nbsp;🔌&thinsp;{{ formatKwh(todayStat.gridImportKwh) }}
          </div>
        </div>
      </div>
      <div class="card-footer d-none d-lg-block d-xl-none text-muted text-center p-1 dash-meta">
        ⚡&thinsp;{{ formatKwh(todayStat.consumptionKwh) }} &nbsp;·&nbsp; 🔌&thinsp;{{ formatKwh(todayStat.gridImportKwh) }}
      </div>
    </div>
  </div>

</div>

<!-- Loading: the cards' shape (phones: full width with the chart's height) -->
<div *ngIf="!solar" class="row g-2 g-sm-3 mb-3">
  <div class="col-6 col-lg-3 dash-chart-col" *ngFor="let _ of [0,1,2,3]">
    <div class="card h-100">
      <div class="card-body p-2 p-sm-3">
        <div class="d-flex align-items-center gap-2">
          <app-skeleton width="2rem" height="2rem" radius="6px" [darkMode]="darkMode" style="flex-shrink:0;"></app-skeleton>
          <div style="flex:1;min-width:0;">
            <app-skeleton width="60%" height=".75rem" radius="4px" [darkMode]="darkMode" style="display:block;margin-bottom:6px;"></app-skeleton>
            <app-skeleton width="80%" height="1.4rem" radius="4px" [darkMode]="darkMode" style="display:block;"></app-skeleton>
          </div>
        </div>
        <app-skeleton class="d-block d-sm-none" width="100%" height="var(--dash-card-chart-h)" radius="4px" [darkMode]="darkMode" style="margin-top:6px;"></app-skeleton>
      </div>
    </div>
  </div>
</div>
